'use client';

import NextLink, { type LinkProps } from 'next/link';
import { type ComponentProps, type ReactNode, type Ref, forwardRef } from 'react';
import useIsExternalLink from '../Link/useIsExternalLink';
import getAnchorRel from '../Link/getAnchorRel';
import type { LinkRel, LinkTarget } from '../../types';

type Props = Omit<LinkProps, 'rel' | 'prefetch'> &
  Omit<ComponentProps<'a'>, 'rel'> & {
    children: ReactNode;
    'data-tooltip-id'?: string;
    prefetch?: true;
    rel?: LinkRel | LinkRel[];
    target?: LinkTarget;
    isNativeLink?: true;
  };

/**
 * formatNextLinkHref formats a given href for use with NextLink.
 *
 * @param { basePath, href }
 * @returns string
 */
function formatNextLinkHref({ basePath, href }: { basePath: string; href: string }) {
  // When an app uses basePath Next automatically prepends that basePath to all its hrefs.
  //
  // > For example, using /about will automatically become /docs/about when basePath is set to /docs.
  // >
  // > https://nextjs.org/docs/app/api-reference/next-config-js/basePath#links
  if (Boolean(basePath)) {
    // This allows us to write hrefs like we're used to where we always manually write out the basePath, and not end up with two basePaths in the href when Next automatically prepends it.
    return href.replace?.(new RegExp(`^${basePath}`), '');
  }

  return href;
}

/**
 * getBasePath returns the basePath of the current app. This is designed to work for both the app and page routers.
 *
 * basePath from useRouter works with page router, but not with app router. basePath is a valid config in both scenarios, but Next.js offers no simple way to access it at run time for app router apps.
 *
 * @returns string
 */
function getBasePath() {
  // If this env ever changes we could make our own BASE_PATH var we read in the next.config.ts, but this already exists and is the simplest solution currently.
  // eslint-disable-next-line turbo/no-undeclared-env-vars
  return process.env.__NEXT_ROUTER_BASEPATH || '';
}

export default forwardRef(function LinkOrNativeAnchor({ children, prefetch, isNativeLink, ...rest }: Props, ref: Ref<HTMLAnchorElement>) {
  const isExternal = useIsExternalLink(rest.href);
  const basePath = getBasePath();

  // Override any props as needed.
  const linkProps = {
    ...rest,
    ref,
    rel: getAnchorRel(rest.rel, rest.target, isExternal)
  };

  // Our historical conflation of <a> and <Link> here and in various helper components (Button, LinkInfoRenderer, LinkButtonOrDiv) makes things simple for callers who just care about getting a link, but it also obfuscates some nuance and intent around client vs non-client navigation.
  // TODO It would probably be more readable if we destroyed LinkOrNativeAnchor in favor of just defining an <a> or Next.js <Link> where needed.
  if (isNativeLink) {
    return (
      <a {...linkProps} href={formatNextLinkHref({ basePath, href: rest.href })} data-linktype="native">
        {children}
      </a>
    );
  }

  return (
    <NextLink
      // Only pass prefetch if it is explicitly set to true. Otherwise we will always disable it and set it to false.
      prefetch={prefetch === true ? true : false}
      {...linkProps}
      href={formatNextLinkHref({ basePath, href: rest.href })}
      data-linktype="next"
    >
      {children}
    </NextLink>
  );
});
